import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { MECHANISMS } from "@/lib/site";
import SEO from "@/components/SEO";

export default function MechanismsIndex() {
  return (
    <div data-testid="mechanisms-index-page">
      <SEO
        title="Mechanisms"
        description="How reinforced concrete deteriorates, chloride attack, carbonation, alkali-silica reaction (ASR), sulphate attack and fire damage. Diagnosis and repair strategies for each mechanism."
      />
      {/* Header */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="tech-label reveal">// Mechanisms · Chemical &amp; Physical Deterioration</div>
          <h1 className="hero-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-6 max-w-5xl reveal reveal-delay-1">
            The chemistry<br />behind the cracks.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-zinc-700 leading-relaxed reveal reveal-delay-2">
            Concrete rarely fails without a mechanism. Some are chemical, some
            physical, some are the aftermath of an event. Every repair
            starts by identifying which one is at play, because the fix
            follows the mechanism.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200">
          {MECHANISMS.map((m) => (
            <Link
              key={m.slug}
              to={`/mechanisms/${m.slug}`}
              className="group relative bg-zinc-950 text-zinc-100 overflow-hidden hover:bg-zinc-900 transition-colors"
              data-testid={`mechanism-card-${m.slug}`}
            >
              {/* Illustration panel */}
              <div className="relative aspect-[16/10] overflow-hidden bg-zinc-950">
                {m.illustration && (
                  <img
                    src={m.illustration}
                    alt={`${m.name} technical schematic`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-70 transition-opacity duration-500 group-hover:opacity-90"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/40 to-zinc-950/30" />

                <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-100 bg-zinc-950/70 px-2 py-1 border border-zinc-100/30">
                      // {m.code}
                    </div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-300 bg-zinc-950/70 px-2 py-1">
                      Mechanism {String(MECHANISMS.indexOf(m) + 1).padStart(2, "0")} / {String(MECHANISMS.length).padStart(2, "0")}
                    </div>
                  </div>

                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#D94515]">
                        Mechanism
                      </div>
                      <div className="font-display text-3xl md:text-4xl lg:text-5xl font-bold uppercase mt-2 leading-[0.95] text-zinc-50">
                        {m.name}
                      </div>
                    </div>
                    <ArrowUpRight
                      size={26}
                      strokeWidth={2}
                      className="text-zinc-300 shrink-0 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#D94515]"
                    />
                  </div>
                </div>
              </div>

              {/* Tagline strip */}
              <div className="border-t border-zinc-800 p-6 md:p-8 bg-zinc-950">
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {m.tagline}
                </p>
                <div className="mt-4 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5 group-hover:text-[#D94515] transition-colors">
                  Read the detail <ChevronRight size={12} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative border border-zinc-900 p-10 md:p-16 bg-zinc-50">
          <span className="corner-tick tl" />
          <span className="corner-tick tr" />
          <span className="corner-tick bl" />
          <span className="corner-tick br" />
          <div className="tech-label">// Not sure which one</div>
          <h3 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight mt-4 leading-[0.95] max-w-3xl">
            That&apos;s what a diagnosis is for.
          </h3>
          <p className="mt-6 max-w-2xl text-zinc-700">
            Send us a photograph and a short description. We&apos;ll tell you
            which mechanism is most likely, what testing is needed to prove it,
            and what a compliant repair would look like.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-primary" data-testid="mechanisms-cta-enquire">
              Start an enquiry <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
